import { emptyFragment } from '@execonline-inc/execonline-ui';
import clsx from 'clsx';
import { just, Maybe, nothing } from 'maybeasy';
import { ActiveProgramModule } from '../../../ProgramStore/Types';
import { T } from '../../../Translations';

type DueState = { kind: 'late'; days: number } | { kind: 'today' } | { kind: 'due'; days: number };

// Modules due within this many days get the urgent colors.
const URGENT_DAYS = 7;

const dueState = (module: ActiveProgramModule): Maybe<DueState> =>
  module.daysSinceModuleEnd
    .andThen<DueState>((days) => (days > 0 ? just({ kind: 'late', days }) : nothing()))
    .orElse(() =>
      module.daysRemaining.map<DueState>((days) =>
        days === 0 ? { kind: 'today' } : { kind: 'due', days },
      ),
    );

const urgent = (state: DueState): boolean => {
  switch (state.kind) {
    case 'late':
    case 'today':
      return true;
    case 'due':
      return state.days <= URGENT_DAYS;
  }
};

const DueText: React.FC<{ state: DueState }> = ({ state }) => {
  switch (state.kind) {
    case 'late':
      return <T kind="Due {{count}} day ago" count={state.days} />;
    case 'today':
      return <T kind="Due today" />;
    case 'due':
      return <T kind="Due in {{count}} day" count={state.days} />;
  }
};

interface Props {
  module: ActiveProgramModule;
}

function ModuleDueBadge({ module }: Props) {
  return dueState(module)
    .map((state) => (
      <span
        className={clsx(
          'rounded-sm px-2 py-0.5 text-sm font-semibold',
          urgent(state) ? 'bg-mistyRose text-carnelian' : 'bg-gray-100 text-gray-700',
        )}
        data-test-module-due={state.kind}
      >
        <DueText state={state} />
      </span>
    ))
    .getOrElse(emptyFragment);
}

export default ModuleDueBadge;
